import { Deferred, Head, Link, router, usePage } from "@inertiajs/react"
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyData = Record<string, any>
import AddIcon from "@mui/icons-material/Add"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import DownloadIcon from "@mui/icons-material/Download"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Popover,
    Skeleton,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { DateCalendar, DatePicker, TimePicker } from "@mui/x-date-pickers"
import dayjs, { Dayjs } from "dayjs"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
    Control,
    Controller,
    SubmitHandler,
    useFieldArray,
    useForm,
    useWatch,
} from "react-hook-form"
import { formatErrorMessages, translateErrorMessage } from "@/lib/formErrors"
import { formatFpcId, formatMoney } from "@/lib/format"
import { addableDayRange, compareByEntryDate, firstAddableDay, isDayTaken, newEntryWithDays, tombstoneDestroyedDays } from "@/lib/reportDays"
import { computeDayHours, computeEntryTotals } from "@/lib/reportHours"
import { IEmployee } from "@/models/employee"
import { IGeneralInfo } from "@/models/generalInfo"
import { IReport, IReportEntry, IReportEntryDay, IReportEntryDayEvent, Report, ReportEntryDay, ReportEntryDayEvent } from "@/models/report"

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
    is_edit: boolean
    report_base?: { id: number; name: string } | null
    report?: IReport | null
    general_infos?: IGeneralInfo[]
    employees?: IEmployee[]
    back_params: Record<string, string | null>
    errors?: Record<string, string[]>
}

// ─── Hour calculation helpers ────────────────────────────────────────────────

function updateEntryTotals(
    entryIndex: number,
    getValues: () => IReport,
    setValue: (name: string, value: unknown) => void,
) {
    const values = getValues()
    const entry = values.report_entries[entryIndex]
    if (!entry) return
    const totals = computeEntryTotals(entry.report_entry_days, values.week_hours, entry.daily_salary)

    setValue(`report_entries.${entryIndex}.worked_hours`, totals.worked_hours)
    setValue(`report_entries.${entryIndex}.extra_hours`, totals.extra_hours)
    setValue(`report_entries.${entryIndex}.double_hours`, totals.double_hours)
    setValue(`report_entries.${entryIndex}.night_hours`, totals.night_hours)
    setValue(`report_entries.${entryIndex}.hourly_salary`, totals.hourly_salary)
    setValue(`report_entries.${entryIndex}.percent_35`, totals.percent_35)
    setValue(`report_entries.${entryIndex}.percent_15`, totals.percent_15)
    setValue(`report_entries.${entryIndex}.double_salary`, totals.double_salary)
    setValue(`report_entries.${entryIndex}.total_amount`, totals.total_amount)
}

function updateAllEntryTotals(
    getValues: () => IReport,
    setValue: (name: string, value: unknown) => void,
) {
    getValues().report_entries.forEach((_, entryIdx) => updateEntryTotals(entryIdx, getValues, setValue))
}

function updateHoursForDay(
    entryIndex: number,
    dayIndex: number,
    getValues: () => IReport,
    setValue: (name: string, value: unknown) => void,
) {
    const values = getValues()
    const day = values.report_entries[entryIndex].report_entry_days[dayIndex]
    const dayHours = computeDayHours(day)

    setValue(`report_entries.${entryIndex}.report_entry_days.${dayIndex}.worked_hours`, dayHours.worked_hours)
    setValue(`report_entries.${entryIndex}.report_entry_days.${dayIndex}.extra_hours`, dayHours.extra_hours)
    setValue(`report_entries.${entryIndex}.report_entry_days.${dayIndex}.double_hours`, dayHours.double_hours)
    setValue(`report_entries.${entryIndex}.report_entry_days.${dayIndex}.night_hours`, dayHours.night_hours)

    updateEntryTotals(entryIndex, getValues, setValue)
}

// ─── EventRow ────────────────────────────────────────────────────────────────

interface EventRowProps {
    control: Control<IReport>
    entryIndex: number
    dayIndex: number
    eventIndex: number
    getValues: () => IReport
    setValue: (name: string, value: unknown) => void
    onRemove: () => void
}

function EventRow({ control, entryIndex, dayIndex, eventIndex, getValues, setValue, onRemove }: EventRowProps) {
    const { t } = useTranslation()
    const destroyed = useWatch({
        control,
        name: `report_entries.${entryIndex}.report_entry_days.${dayIndex}.report_entry_day_events.${eventIndex}._destroy` as keyof IReport,
    })
    if (destroyed) return null

    const base = `report_entries.${entryIndex}.report_entry_days.${dayIndex}.report_entry_day_events.${eventIndex}`

    const anchorToEntryDate = (val: ReturnType<typeof dayjs> | null): string | null => {
        if (!val) return null
        const entryDate = dayjs(getValues().report_entries[entryIndex].report_entry_days[dayIndex].entry_date as unknown as string)
        return entryDate.hour(val.hour()).minute(val.minute()).second(0).millisecond(0).toISOString()
    }

    const handleTimeChange = () => {
        updateHoursForDay(entryIndex, dayIndex, getValues, setValue)
    }

    return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Controller
                control={control}
                name={`${base}.start_time` as keyof IReport}
                render={({ field }) => (
                    <TimePicker
                        label={t("weeklyReports.form.start")}
                        value={field.value ? dayjs(field.value as string) : null}
                        onChange={val => { field.onChange(anchorToEntryDate(val)); handleTimeChange() }}
                        slotProps={{ textField: { size: "small", sx: { width: 130 } } }}
                    />
                )}
            />
            <Controller
                control={control}
                name={`${base}.end_time` as keyof IReport}
                render={({ field }) => (
                    <TimePicker
                        label={t("weeklyReports.form.end")}
                        value={field.value ? dayjs(field.value as string) : null}
                        onChange={val => { field.onChange(anchorToEntryDate(val)); handleTimeChange() }}
                        slotProps={{ textField: { size: "small", sx: { width: 130 } } }}
                    />
                )}
            />
            <Tooltip title={t("common.remove")}>
                <IconButton size="small" color="error" onClick={onRemove}>
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    )
}

// ─── DayBlock ────────────────────────────────────────────────────────────────

interface DayBlockProps {
    control: Control<IReport>
    entryIndex: number
    dayIndex: number
    getValues: () => IReport
    setValue: (name: string, value: unknown) => void
    onRemove: () => void
}

function DayBlock({ control, entryIndex, dayIndex, getValues, setValue, onRemove }: DayBlockProps) {
    const { t } = useTranslation()
    const base = `report_entries.${entryIndex}.report_entry_days.${dayIndex}`

    const { fields: eventFields, append: eventAppend, remove: eventRemove } = useFieldArray({
        control,
        name: `report_entries.${entryIndex}.report_entry_days.${dayIndex}.report_entry_day_events`,
    })

    const entryDate  = useWatch({ control, name: `${base}.entry_date` as keyof IReport })
    const workedH    = useWatch({ control, name: `${base}.worked_hours` as keyof IReport })
    const extraH     = useWatch({ control, name: `${base}.extra_hours` as keyof IReport })
    const doubleH    = useWatch({ control, name: `${base}.double_hours` as keyof IReport })
    const nightH     = useWatch({ control, name: `${base}.night_hours` as keyof IReport })
    const eventsWatch = useWatch({ control, name: `${base}.report_entry_day_events` as keyof IReport }) as unknown as IReportEntryDayEvent[]
    const destroyed   = useWatch({ control, name: `${base}._destroy` as keyof IReport })

    const activeEvents   = eventsWatch?.filter(e => !e._destroy) ?? []
    const hasSingleEvent = activeEvents.length === 1
    const hasMissingTimes = activeEvents.some(e => !e.start_time || !e.end_time)
    const hasInvalidRange = activeEvents.some(e =>
        !!e.start_time && !!e.end_time && dayjs(e.end_time) <= dayjs(e.start_time))

    const dayDate  = dayjs(entryDate as string)
    const dayOfWeek = dayDate.day()
    const isSunday  = dayOfWeek === 0

    const handleAddEvent = () => {
        const newEvent = new ReportEntryDayEvent({ start_time: null, end_time: null })
        eventAppend(newEvent)
    }

    const handleRemoveEvent = (evtIdx: number) => {
        const events = getValues().report_entries[entryIndex].report_entry_days[dayIndex].report_entry_day_events
        const evt = events[evtIdx]
        if (evt.id && evt.id > 0) {
            setValue(`${base}.report_entry_day_events.${evtIdx}._destroy`, true)
        } else {
            eventRemove(evtIdx)
        }
        setTimeout(() => updateHoursForDay(entryIndex, dayIndex, getValues, setValue), 0)
    }

    const hoursChips = [
        { label: `W: ${(workedH as number || 0).toFixed(2)}`, color: "default" as const },
        ...(((extraH as number) || 0) > 0 ? [{ label: `E: ${(extraH as number).toFixed(2)}`, color: "warning" as const }] : []),
        ...(((doubleH as number) || 0) > 0 ? [{ label: `D: ${(doubleH as number).toFixed(2)}`, color: "error" as const }] : []),
        ...(((nightH as number) || 0) > 0 ? [{ label: `N: ${(nightH as number).toFixed(2)}`, color: "info" as const }] : []),
    ]

    if (destroyed) return null

    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", flexGrow: 1 }}>
                    {dayDate.format("dddd DD, MMMM YYYY")}
                </Typography>
                {hoursChips.map(c => (
                    <Chip key={c.label} label={c.label} size="small" color={c.color} variant="outlined" />
                ))}
                {!isSunday && (
                    <Controller
                        control={control}
                        name={`${base}.is_holiday` as keyof IReport}
                        render={({ field }) => (
                            <FormControlLabel
                                label={<Typography variant="caption">{t("weeklyReports.form.holiday")}</Typography>}
                                sx={{ m: 0 }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={!!field.value}
                                        onChange={e => {
                                            field.onChange(e.target.checked)
                                            setTimeout(() => updateHoursForDay(entryIndex, dayIndex, getValues, setValue), 0)
                                        }}
                                    />
                                }
                            />
                        )}
                    />
                )}
                <Tooltip title={t("weeklyReports.form.removeDay")}>
                    <IconButton size="small" color="error" onClick={onRemove}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {eventFields.map((eventField, evtIdx) => (
                    <EventRow
                        key={eventField.id}
                        control={control}
                        entryIndex={entryIndex}
                        dayIndex={dayIndex}
                        eventIndex={evtIdx}
                        getValues={getValues}
                        setValue={setValue}
                        onRemove={() => handleRemoveEvent(evtIdx)}
                    />
                ))}
                <Box>
                    <Button size="small" startIcon={<AddIcon />} onClick={handleAddEvent}>
                        {t("weeklyReports.form.addEvent")}
                    </Button>
                </Box>
            </Box>
            {hasSingleEvent && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    {t("weeklyReports.form.singleEventWarning")}
                </Alert>
            )}
            {hasMissingTimes && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {t("weeklyReports.form.missingTimesError")}
                </Alert>
            )}
            {hasInvalidRange && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {t("weeklyReports.form.invalidTimeRangeError")}
                </Alert>
            )}
        </Paper>
    )
}

// ─── AddDayButton ─────────────────────────────────────────────────────────────

interface AddDayButtonProps {
    control: Control<IReport>
    entryIndex: number
    onAdd: (date: Dayjs) => void
}

// Watches the report range and the entry's days on its own so the whole
// accordion doesn't re-render on every punch edit.
function AddDayButton({ control, entryIndex, onAdd }: AddDayButtonProps) {
    const { t } = useTranslation()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const startDate = useWatch({ control, name: "start_date" })
    const endDate   = useWatch({ control, name: "end_date" })
    const days      = (useWatch({
        control,
        name: `report_entries.${entryIndex}.report_entry_days` as keyof IReport,
    }) ?? []) as unknown as IReportEntryDay[]

    const range     = addableDayRange(startDate, endDate)
    const firstFree = firstAddableDay(days, startDate, endDate)
    const disabledReason = !range
        ? t("weeklyReports.form.addDayNoRange")
        : !firstFree ? t("weeklyReports.form.addDayRangeFull") : ""

    return (
        <>
            <Tooltip title={disabledReason}>
                <span>
                    <Button size="small" startIcon={<AddIcon />} disabled={!firstFree}
                        onClick={e => setAnchorEl(e.currentTarget)}>
                        {t("weeklyReports.form.addDay")}
                    </Button>
                </span>
            </Tooltip>
            <Popover
                open={anchorEl !== null && range !== null}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                {range && (
                    <DateCalendar
                        value={null}
                        views={["day"]}
                        minDate={range.min}
                        maxDate={range.max}
                        referenceDate={firstFree ?? range.min}
                        shouldDisableDate={date => isDayTaken(date, days)}
                        onChange={date => {
                            setAnchorEl(null)
                            if (date) onAdd(date)
                        }}
                    />
                )}
            </Popover>
        </>
    )
}

// ─── EntryAccordion ───────────────────────────────────────────────────────────

interface EntryAccordionProps {
    control: Control<IReport>
    entryIndex: number
    employees: IEmployee[]
    employeesLoading: boolean
    getValues: () => IReport
    setValue: (name: string, value: unknown) => void
    onRemove: () => void
}

function EntryAccordion({
    control, entryIndex, employees, employeesLoading,
    getValues, setValue, onRemove,
}: EntryAccordionProps) {
    const { t } = useTranslation()
    const base = `report_entries.${entryIndex}`
    const employee   = useWatch({ control, name: `${base}.employee` as keyof IReport }) as IEmployee | null
    const workedH    = useWatch({ control, name: `${base}.worked_hours` as keyof IReport }) as number
    const extraH     = useWatch({ control, name: `${base}.extra_hours` as keyof IReport }) as number
    const doubleH    = useWatch({ control, name: `${base}.double_hours` as keyof IReport }) as number
    const nightH     = useWatch({ control, name: `${base}.night_hours` as keyof IReport }) as number

    const { fields: dayFields, append: dayAppend } = useFieldArray({
        control,
        name: `report_entries.${entryIndex}.report_entry_days` as unknown as "report_entries",
    })

    const handleAddDay = (date: Dayjs) => {
        if (isDayTaken(date, getValues().report_entries[entryIndex].report_entry_days)) return
        // Spread into a plain object: RHF doesn't clone class instances, so
        // watchers of this day's events would keep receiving the same array.
        const newDay = { ...new ReportEntryDay({ entry_date: date.startOf("day").toDate() }) }
        dayAppend(newDay as unknown as IReportEntry)
    }

    // Unsaved days are flagged too instead of spliced out: RHF watchers keep
    // the path they mounted with, so shifting the later days' indexes would
    // freeze them. buildSubmitData drops unsaved flagged days.
    const handleRemoveDay = (dayIdx: number) => {
        setValue(`${base}.report_entry_days.${dayIdx}._destroy`, true)
        setTimeout(() => updateEntryTotals(entryIndex, getValues, setValue), 0)
    }

    const handleEmployeeChange = (val: IEmployee | null) => {
        setValue(`${base}.employee`, val)
        setValue(`${base}.employee_id`, val?.id || 0)
        // Update daily salary from general info
        const gi = getValues().general_info
        const info = gi?.general_info_entries?.find(e => e.employee_id === val?.id)
        const daily = info?.daily_salary || 0
        setValue(`${base}.daily_salary`, daily)
        // Recalculate all days
        const days = getValues().report_entries[entryIndex].report_entry_days
        days.forEach((_, dayIdx) => updateHoursForDay(entryIndex, dayIdx, getValues, setValue))
    }

    const summaryName = employee ? `${employee.name} ${employee.last_name}` : t("weeklyReports.form.noEmployee")

    return (
        <Accordion defaultExpanded disableGutters variant="outlined" sx={{ "&:before": { display: "none" }, mb: 1 }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    flexDirection: "row-reverse",
                    px: 2,
                    minHeight: 52,
                    "& .MuiAccordionSummary-content": { my: 0, alignItems: "center", gap: 1.5, ml: 1 },
                }}
            >
                {employee ? (
                    <Chip label={formatFpcId(employee.fpc_id)} size="small" color="info" variant="outlined"
                        sx={{ fontFamily: "monospace", fontSize: "0.72rem", flexShrink: 0 }} />
                ) : (
                    <Chip label="—" size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                )}
                <Typography sx={{ flexGrow: 1, fontWeight: employee ? 500 : 400, fontSize: "0.9rem",
                    color: employee ? "text.primary" : "text.disabled" }}>
                    {summaryName}
                </Typography>
                {workedH > 0 && <Chip label={`W: ${workedH.toFixed(2)}`} size="small" variant="outlined" sx={{ flexShrink: 0 }} />}
                {extraH  > 0 && <Chip label={`E: ${extraH.toFixed(2)}`}  size="small" color="warning" variant="outlined" sx={{ flexShrink: 0 }} />}
                {doubleH > 0 && <Chip label={`D: ${doubleH.toFixed(2)}`} size="small" color="error"   variant="outlined" sx={{ flexShrink: 0 }} />}
                {nightH  > 0 && <Chip label={`N: ${nightH.toFixed(2)}`}  size="small" color="info"    variant="outlined" sx={{ flexShrink: 0 }} />}
                <Tooltip title={t("common.removeEntry")}>
                    <IconButton size="small" color="error" sx={{ flexShrink: 0 }}
                        onClick={e => { e.stopPropagation(); onRemove() }}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pb: 2, pt: 1 }}>
                <Box sx={{ mb: 2 }}>
                    <Controller
                        control={control}
                        name={`${base}.employee` as keyof IReport}
                        render={({ field: { value } }) => (
                            <Autocomplete
                                options={employees}
                                value={(value as IEmployee | null) ?? null}
                                loading={employeesLoading}
                                isOptionEqualToValue={(op, v) => op.id === v.id}
                                getOptionLabel={op => `${formatFpcId(op.fpc_id)} ${op.name} ${op.last_name}`}
                                onChange={(_, v) => handleEmployeeChange(v)}
                                renderInput={params => <TextField {...params} label={t("common.employee")} size="small" />}
                                sx={{ maxWidth: 400 }}
                            />
                        )}
                    />
                </Box>
                {[...dayFields.keys()]
                    .sort((a, b) => compareByEntryDate(
                        dayFields[a] as unknown as IReportEntryDay,
                        dayFields[b] as unknown as IReportEntryDay,
                    ))
                    .map(dayIdx => (
                        // Keyed by the field id, not the sorted position: each
                        // block owns its day's events array.
                        <DayBlock
                            key={dayFields[dayIdx].id}
                            control={control}
                            entryIndex={entryIndex}
                            dayIndex={dayIdx}
                            getValues={getValues}
                            setValue={setValue}
                            onRemove={() => handleRemoveDay(dayIdx)}
                        />
                    ))}
                <AddDayButton control={control} entryIndex={entryIndex} onAdd={handleAddDay} />
            </AccordionDetails>
        </Accordion>
    )
}

// ─── HoursTable ──────────────────────────────────────────────────────────────

function HoursTable({ control }: { control: Control<IReport> }) {
    const { t } = useTranslation()
    const entries = useWatch({ control, name: "report_entries" }) as IReportEntry[]

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                    <TableRow>
                        {[
                            { key: "employee", label: t("common.employee") },
                            { key: "daily", label: t("weeklyReports.form.daily") },
                            { key: "hourly", label: t("weeklyReports.form.hourly") },
                            { key: "35", label: "35%" },
                            { key: "15", label: "15%" },
                            { key: "double", label: t("weeklyReports.form.doubleHrs") },
                            { key: "extraHrs", label: t("weeklyReports.form.extraHrs") },
                            { key: "doubleHrs", label: t("weeklyReports.form.doubleHrs") },
                            { key: "nightHrs", label: t("weeklyReports.form.nightHrs") },
                            { key: "total", label: t("common.total") },
                        ].map(h => (
                            <TableCell key={h.key} align={h.key === "employee" ? "left" : "right"} sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>{h.label}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries?.filter(e => !e._destroy).map((entry, i) => (
                        <TableRow key={i} hover>
                            <TableCell>{entry.employee ? `${entry.employee.name} ${entry.employee.last_name}` : "—"}</TableCell>
                            <TableCell align="right">{formatMoney(entry.daily_salary)}</TableCell>
                            <TableCell align="right">{formatMoney(entry.hourly_salary)}</TableCell>
                            <TableCell align="right">{formatMoney(entry.percent_35)}</TableCell>
                            <TableCell align="right">{formatMoney(entry.percent_15)}</TableCell>
                            <TableCell align="right">{formatMoney(entry.double_salary)}</TableCell>
                            <TableCell align="right">{(entry.extra_hours || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">{(entry.double_hours || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">{(entry.night_hours || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">{formatMoney(entry.total_amount)}</TableCell>
                        </TableRow>
                    ))}
                    {(!entries || entries.filter(e => !e._destroy).length === 0) && (
                        <TableRow>
                            <TableCell colSpan={10} align="center" sx={{ py: 4, color: "text.secondary" }}>{t("weeklyReports.form.noEntries")}</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

// ─── DetailsTable ─────────────────────────────────────────────────────────────

function DetailsTable({ control }: { control: Control<IReport> }) {
    const { t } = useTranslation()
    const entries = useWatch({ control, name: "report_entries" }) as IReportEntry[]

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {entries?.filter(e => !e._destroy).map((entry, i) => {
                const daysWithEvents = entry.report_entry_days?.filter(
                    d => !d._destroy && d.report_entry_day_events?.some(ev => !ev._destroy)
                ).sort(compareByEntryDate) ?? []
                return (
                    <Paper key={i} variant="outlined">
                        <Box sx={{ px: 2, py: 1, bgcolor: "action.hover" }}>
                            <Typography sx={{ fontWeight: "bold" }}>
                                {entry.employee ? `${formatFpcId(entry.employee.fpc_id)} ${entry.employee.name} ${entry.employee.last_name}` : "—"}
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: "bold" }}>{t("common.date")}</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>{t("weeklyReports.form.punchIn")}</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>{t("weeklyReports.form.punchOut")}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>{t("weeklyReports.form.regularHrs")}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>{t("weeklyReports.form.doubleHrs")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {daysWithEvents.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>{t("weeklyReports.form.noEvents")}</TableCell>
                                        </TableRow>
                                    ) : daysWithEvents.map((day, di) => {
                                        const activeEvents = day.report_entry_day_events.filter(e => !e._destroy)
                                        return activeEvents.map((evt, ei) => (
                                            <TableRow key={`${di}-${ei}`} hover>
                                                {ei === 0 && (
                                                    <TableCell rowSpan={activeEvents.length}>
                                                        {dayjs(day.entry_date).format("ddd DD/MM/YYYY")}
                                                    </TableCell>
                                                )}
                                                <TableCell>{evt.start_time ? dayjs(evt.start_time).format("hh:mm A") : "—"}</TableCell>
                                                <TableCell>{evt.end_time   ? dayjs(evt.end_time).format("hh:mm A")   : "—"}</TableCell>
                                                {ei === 0 && (
                                                    <>
                                                        <TableCell align="right" rowSpan={activeEvents.length}>{(day.worked_hours || 0).toFixed(2)}</TableCell>
                                                        <TableCell align="right" rowSpan={activeEvents.length}>{(day.double_hours || 0).toFixed(2)}</TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )
            })}
            {(!entries || entries.filter(e => !e._destroy).length === 0) && (
                <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>{t("weeklyReports.form.noEntries")}</Typography>
            )}
        </Box>
    )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function WeeklyReportFormPage() {
    const {
        is_edit: isEdit,
        report_base,
        report,
        general_infos: rawGeneralInfos,
        employees: rawEmployees,
        back_params,
        errors: serverErrors,
    } = usePage<Props>().props
    const { t } = useTranslation()

    const generalInfos   = rawGeneralInfos ?? []
    const employees      = rawEmployees ?? []
    const employeesLoading = rawEmployees === undefined
    const deferredLoading = report === undefined || rawGeneralInfos === undefined || employeesLoading

    const originalReport = useRef<IReport | null>(null)

    const { control, reset, handleSubmit, setValue: setValueRaw, getValues } = useForm<IReport>({
        defaultValues: report
            ? { ...report }
            : report_base
            ? { ...new Report(), ...report_base }
            // Spread so RHF clones the form state (it shares class instances).
            : { ...new Report() },
    })

    // react-hook-form setValue doesn't have a narrowed type for dynamic paths
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setValue = setValueRaw as (name: string, value: any) => void

    const { fields: entryFields, append: entryAppend, remove: entryRemove, update: entryUpdate } = useFieldArray({
        control,
        name: "report_entries",
    })

    const [submitting, setSubmitting] = useState(false)
    const [localErrors, setLocalErrors] = useState<Record<string, string[]>>({})
    const [activeTab, setActiveTab] = useState(0)

    // Load report data when deferred prop arrives, then recalculate all hours
    const reportKey = `${report?.id}-${report?.updated_at}`
    useEffect(() => {
        if (!report) return
        originalReport.current = report
        reset(report as IReport)
        setTimeout(() => {
            const values = getValues()
            values.report_entries.forEach((entry, entryIdx) => {
                entry.report_entry_days.forEach((_, dayIdx) => {
                    updateHoursForDay(entryIdx, dayIdx, getValues, setValue)
                })
            })
        }, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportKey])

    useEffect(() => {
        if (serverErrors && Object.keys(serverErrors).length > 0) setLocalErrors(serverErrors)
    }, [serverErrors])

    // Update daily salaries when general_infos load
    useEffect(() => {
        if (!generalInfos || generalInfos.length === 0) return
        const gi = getValues("general_info") as IGeneralInfo | null
        if (!gi) return
        const fullGI = generalInfos.find(g => g.id === gi.id)
        if (!fullGI) return
        const entries = getValues("report_entries")
        entries.forEach((entry, idx) => {
            const info = fullGI.general_info_entries?.find(e => e.employee_id === entry.employee_id)
            if (!info) return
            setValue(`report_entries.${idx}.daily_salary`, info.daily_salary)
            updateEntryTotals(idx, getValues, setValue)
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generalInfos.length])

    const handleAddEntry = () => {
        const values = getValues()
        entryAppend(newEntryWithDays(values.start_date, values.end_date))
    }

    const handleRemoveEntry = (index: number) => {
        const entry = getValues().report_entries[index]
        if (entry.id && entry.id > 0) {
            // update() (not setValue) so the field array notifies its
            // subscribers and the tombstoned accordion disappears right away.
            entryUpdate(index, { ...entry, _destroy: true })
        } else {
            entryRemove(index)
        }
    }

    const handleGeneralInfoChange = (gi: IGeneralInfo | null) => {
        setValue("general_info", gi)
        setValue("general_info_id", gi?.id || 0)
        const entries = getValues().report_entries
        entries.forEach((entry, idx) => {
            const info = gi?.general_info_entries?.find(e => e.employee_id === entry.employee_id)
            const daily = info?.daily_salary || 0
            setValue(`report_entries.${idx}.daily_salary`, daily)
            entry.report_entry_days.forEach((_, dayIdx) => {
                updateHoursForDay(idx, dayIdx, getValues, setValue)
            })
        })
    }

    const buildSubmitData = (values: IReport): IReport => {
        const data = JSON.parse(JSON.stringify(values))
        data.report_entries.forEach((e: IReportEntry) => {
            e.report_entry_days = tombstoneDestroyedDays(e.report_entry_days ?? []) as IReportEntryDay[]
        })
        if (isEdit && originalReport.current) {
            const currentIds = data.report_entries
                .filter((e: IReportEntry) => !e._destroy)
                .map((e: IReportEntry) => e.id)
                .filter((id: number) => id > 0)

            originalReport.current.report_entries.forEach((orig: IReportEntry) => {
                if (!currentIds.includes(orig.id)) {
                    data.report_entries.push({ id: orig.id, _destroy: true })
                } else {
                    const cur = data.report_entries.find((e: IReportEntry) => e.id === orig.id)
                    if (cur) {
                        // Tombstones count as present so they aren't sent twice.
                        const curDayIds = cur.report_entry_days
                            .map((d: IReportEntryDay) => d.id)
                            .filter((id: number) => id > 0)
                        orig.report_entry_days?.forEach((od: IReportEntryDay) => {
                            if (!curDayIds.includes(od.id))
                                cur.report_entry_days.push({ id: od.id, _destroy: true })
                        })
                    }
                }
            })
        }
        return data
    }

    const submit = (values: IReport, stay = false) => {
        setSubmitting(true)
        setLocalErrors({})
        const submitData = buildSubmitData(values)
        const cleanBack = Object.fromEntries(Object.entries(back_params).filter(([, v]) => v != null)) as Record<string, string>
        const backQuery = new URLSearchParams(cleanBack).toString()
        const url = isEdit
            ? `/weekly_reports/${report!.id}?${backQuery}`
            : `/weekly_reports?${backQuery}`

        const originalUrl = window.location.pathname + window.location.search

        const onError = (errors: Record<string, string | string[]>) => {
            window.history.replaceState(window.history.state, "", originalUrl)
            setLocalErrors(Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])))
            setSubmitting(false)
        }
        const onSuccess = () => setSubmitting(false)
        if (isEdit && stay) {
            router.patch(url, { report: submitData, stay: 1 } as AnyData, {
                only: ["report"],
                preserveState: true,
                preserveScroll: true,
                preserveUrl: true,
                onError,
                onSuccess,
            })
        } else if (isEdit) {
            router.patch(url, { report: submitData } as AnyData, { onError, onSuccess })
        } else if (stay) {
            router.post(url, { report: submitData, stay: 1 } as AnyData, { preserveScroll: true, onError, onSuccess })
        } else {
            router.post(url, { report: submitData } as AnyData, {
                onError,
                onSuccess: () => { sessionStorage.removeItem('scroll_weekly_reports'); onSuccess() },
            })
        }
    }

    const onSubmit: SubmitHandler<IReport> = values => submit(values, false)
    const onSubmitAndStay: SubmitHandler<IReport> = values => submit(values, true)

    const [downloading, setDownloading] = useState(false)
    const [downloadError, setDownloadError] = useState(false)
    const handleDownloadExcel = async () => {
        setDownloading(true)
        setDownloadError(false)
        const values = getValues()
        const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ""
        try {
            const res = await fetch("/weekly_reports/generate_excel", {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
                body: JSON.stringify({ report: values }),
            })
            if (!res.ok) {
                setDownloadError(true)
                return
            }
            const blob = await res.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `${values.name || "report"}.xlsx`
            a.click()
            URL.revokeObjectURL(url)
        } catch {
            setDownloadError(true)
        } finally {
            setDownloading(false)
        }
    }

    const cleanBack = Object.fromEntries(Object.entries(back_params).filter(([, v]) => v != null)) as Record<string, string>
    const backUrl = `/weekly_reports?${new URLSearchParams(cleanBack).toString()}`
    const errorMessages = formatErrorMessages(t, localErrors)

    return (
        <>
            <Head title={isEdit ? t("weeklyReports.form.editTitle") : t("weeklyReports.form.newTitle")} />

            <Box sx={{
                bgcolor: "background.default",
                borderBottom: 1,
                borderColor: "divider",
            }}>
                <Container maxWidth="xl">
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, py: 1 }}>
                    <Tooltip title={t("weeklyReports.form.backTooltip")}>
                        <IconButton LinkComponent={Link} href={backUrl} size="small">
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "flex" } }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", flexGrow: 1, display: { xs: "none", sm: "block" } }}>
                        {isEdit ? (report_base?.name || report?.name || t("weeklyReports.form.editTitle")) : t("weeklyReports.form.newTitle")}
                    </Typography>
                    <Box sx={{ flexGrow: { xs: 1, sm: 0 } }} />
                    <Tooltip title={downloading ? t("weeklyReports.form.generating") : t("weeklyReports.form.downloadExcel")}>
                        <span>
                            <IconButton
                                color="success"
                                size="small"
                                onClick={handleDownloadExcel}
                                disabled={downloading || submitting}
                                sx={{ display: { xs: "inline-flex", sm: "none" } }}
                            >
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadExcel}
                        disabled={downloading || submitting}
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                    >
                        {downloading ? t("weeklyReports.form.generating") : t("weeklyReports.form.downloadExcel")}
                    </Button>
                    <Button color="inherit" LinkComponent={Link} href={backUrl} disabled={submitting || deferredLoading} size="small">
                        {t("common.cancel")}
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleSubmit(onSubmitAndStay)} disabled={submitting || deferredLoading}>
                        {t("common.saveAndContinue")}
                    </Button>
                    <Button variant="contained" size="small" onClick={handleSubmit(onSubmit)} disabled={submitting || deferredLoading}>
                        {submitting ? t("common.saving") : t("common.save")}
                    </Button>
                </Box>
                </Container>
            </Box>

            <Container maxWidth="xl" sx={{ py: 3 }}>
                {errorMessages.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>{errorMessages.join(" · ")}</Alert>
                )}

                {downloadError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(false)}>
                        {t("common.downloadError")}
                    </Alert>
                )}

                {/* ── Basic fields ── */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "3fr 2fr 1fr 1fr 1fr 1fr" }, gap: 2 }}>
                        <Controller
                            control={control}
                            name="name"
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("common.name")}
                                    size="small"
                                    fullWidth
                                    error={!!localErrors.name}
                                    helperText={translateErrorMessage(t, localErrors.name)}
                                />
                            )}
                        />
                        <Deferred data="general_infos" fallback={<Skeleton variant="rounded" height={40} />}>
                            <Controller
                                control={control}
                                name="general_info"
                                render={({ field: { value } }) => (
                                    <Autocomplete
                                        options={generalInfos}
                                        value={(value as IGeneralInfo | null) ?? null}
                                        isOptionEqualToValue={(op, v) => op.id === v.id}
                                        getOptionLabel={op => op.name || ""}
                                        onChange={(_, v) => handleGeneralInfoChange(v)}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label={t("common.generalInfo")}
                                                size="small"
                                                error={!!localErrors.general_info}
                                                helperText={translateErrorMessage(t, localErrors.general_info)}
                                            />
                                        )}
                                        fullWidth
                                    />
                                )}
                            />
                        </Deferred>
                        <Controller
                            control={control}
                            name="week_hours"
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("weeklyReports.form.weekHours")}
                                    size="small"
                                    fullWidth
                                    slotProps={{ htmlInput: { inputMode: "decimal" } }}
                                    onChange={e => {
                                        field.onChange(Number(e.target.value) || 0)
                                        updateAllEntryTotals(getValues, setValue)
                                    }}
                                    error={!!localErrors.week_hours}
                                    helperText={translateErrorMessage(t, localErrors.week_hours)}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="start_date"
                            render={({ field }) => (
                                <DatePicker
                                    label={t("common.startDate")}
                                    value={field.value ? dayjs(field.value as unknown as string) : null}
                                    onChange={val => field.onChange(val?.toISOString() ?? null)}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            error: !!localErrors.start_date,
                                            helperText: translateErrorMessage(t, localErrors.start_date),
                                        },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="end_date"
                            render={({ field }) => (
                                <DatePicker
                                    label={t("common.endDate")}
                                    value={field.value ? dayjs(field.value as unknown as string) : null}
                                    onChange={val => field.onChange(val?.toISOString() ?? null)}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            error: !!localErrors.end_date,
                                            helperText: translateErrorMessage(t, localErrors.end_date),
                                        },
                                    }}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="report_date"
                            render={({ field }) => (
                                <DatePicker
                                    label={t("common.reportDate")}
                                    value={field.value ? dayjs(field.value as unknown as string) : null}
                                    onChange={val => field.onChange(val?.toISOString() ?? null)}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            error: !!localErrors.report_date,
                                            helperText: translateErrorMessage(t, localErrors.report_date),
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Paper>

                {/* ── Tabs ── */}
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                        <Tab label={t("weeklyReports.form.tabEmployees")} />
                        <Tab label={t("weeklyReports.form.tabHours")} />
                        <Tab label={t("weeklyReports.form.tabDetails")} />
                    </Tabs>
                </Box>

                {/* Employees tab */}
                {activeTab === 0 && (
                    <Deferred data={["report", "employees"]} fallback={
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={52} />)}
                        </Box>
                    }>
                        <Box>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddEntry}>
                                    {t("common.addEntry")}
                                </Button>
                            </Box>
                            {entryFields.map((field, idx) => {
                                const entry = getValues().report_entries[idx]
                                if (entry?._destroy) return null
                                const entryId = entry?.id
                                // The index is in the key so an entry remounts when it
                                // shifts (removing an unsaved entry, or Save & Continue
                                // returning fewer entries): RHF watchers keep their
                                // mount-time path. The id keeps an entry from inheriting
                                // another one's day list.
                                const entryKey = `${typeof entryId === "number" && entryId > 0 ? entryId : field.id}-${idx}`
                                return (
                                    <EntryAccordion
                                        key={entryKey}
                                        control={control}
                                        entryIndex={idx}
                                        employees={employees}
                                        employeesLoading={employeesLoading}
                                        getValues={getValues}
                                        setValue={setValue}
                                        onRemove={() => handleRemoveEntry(idx)}
                                    />
                                )
                            })}
                            {entryFields.length === 0 && (
                                <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                                    No entries. Add an employee entry to get started.
                                </Typography>
                            )}
                        </Box>
                    </Deferred>
                )}

                {/* Hours tab */}
                {activeTab === 1 && <HoursTable control={control} />}

                {/* Details tab */}
                {activeTab === 2 && <DetailsTable control={control} />}
            </Container>
        </>
    )
}
